import type { Nullable } from '@quesmed/types';
import { IAccessLevel, type IPayload } from '@quesmed/types/models';
import { create } from 'zustand';
import type { OmitFunctions } from '@/types';
import { createSelectors } from './selector';

interface AuthState {
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  isAuthPending: boolean;
  token: Nullable<string>;
  tokenDecoded: Nullable<IPayload>;
  isWriter: boolean;
  isSalesRole: boolean;
  demoUser: boolean | null;
  accessLevel: IAccessLevel | null;
  isAdmin: boolean;
  isSeniorAdmin: boolean;
  isEditor: boolean;
  isSeniorEditor: boolean;
  reset: () => void;
  setIsAuthenticated: (value: boolean) => void;
  setIsAuthPending: (value: boolean) => void;
  setIsSuperAdmin: (value: boolean) => void;
  setIsSalesRole: (value: boolean) => void;
  setToken: (payload: Nullable<string>) => void;
  setTokenDecoded: (value: Nullable<IPayload>) => void;
  setDemoUser: (value: boolean) => void;
  setAccessLevel: (value: IAccessLevel | null) => void;
}

const initialState: OmitFunctions<AuthState> = {
  accessLevel: null,
  isAdmin: false,
  isSeniorAdmin: false,
  isSeniorEditor: false,
  isWriter: false,
  isEditor: false,
  isAuthenticated: false,
  isSuperAdmin: false,
  isAuthPending: true,
  token: null,
  tokenDecoded: null,
  isSalesRole: false,
  demoUser: null,
};

export const authState = create<AuthState>()((set) => ({
  ...initialState,
  reset: () => {
    set(initialState);
  },
  setAccessLevel: (accessLevel) =>
    set({
      accessLevel,
      isAdmin: accessLevel
        ? [IAccessLevel.ADMINISTRATOR, IAccessLevel.SUPER_ADMIN].includes(accessLevel)
        : false,
      isSeniorAdmin: accessLevel
        ? [IAccessLevel.ADMINISTRATOR, IAccessLevel.SUPER_ADMIN, IAccessLevel.SENIOR_EDITOR].includes(
            accessLevel,
          )
        : false,
      isWriter: accessLevel === IAccessLevel.WRITER,
      isSalesRole: accessLevel === IAccessLevel.MARKETING,
      isSuperAdmin: accessLevel === IAccessLevel.SUPER_ADMIN,
      isEditor: accessLevel === IAccessLevel.EDITOR,
      isSeniorEditor: accessLevel === IAccessLevel.SENIOR_EDITOR,
    }),
  setIsSalesRole: (isSalesRole) => set({ isSalesRole }),
  setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
  setIsAuthPending: (isAuthPending) => set({ isAuthPending }),
  setTokenDecoded: (tokenDecoded) => set({ tokenDecoded }),
  setToken: (token) => set({ token }),
  setIsSuperAdmin: (isSuperAdmin) => set({ isSuperAdmin }),
  setDemoUser: (demoUser) => set({ demoUser }),
}));

export const useAuthState = () => authState((state) => state);
export const useAuth = createSelectors(authState);
